import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Button,
  FormControl,
  FormLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  VStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Text,
  Flex,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  TableContainer,
  Badge,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import { sileo } from 'sileo';
import { ResponsiveTableContainer } from '../ui/ResponsiveTableContainer';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../lib/firebase';
import { LeagueId } from '../../types';
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { Match } from '../../types';
import {
  mundialBracketService,
  isKnockoutDrawNeedingPenaltyWinner,
} from '../../services/mundial/mundialBracketService';
import { mundialMatchService } from '../../services/mundial/mundialMatchService';
import { hydrateMundialMatchesAsync } from '../../services/mundial/mundialKnockoutHydrationService';
import {
  KnockoutPenaltyWinnerDialog,
  PenaltyWinnerRequest,
} from './KnockoutPenaltyWinnerDialog';
import {
  fetchWorldCup2026Events,
  syncWorldCupResultsFromEvents,
  SyncWorldCupResultsSummary,
} from '../../services/mundial/theSportsDbService';
import {
  filterActiveMundialMatches,
  getActiveMundialJornadas,
  getActiveMundialKnockoutPhases,
  formatMundialCaptureLabel,
  getMundialGroupMatches,
  getMundialKnockoutMatches,
  groupMundialKnockoutByPhase,
  groupMundialMatchesByJornada,
  type MundialJornadaCaptureBlock,
  type MundialKnockoutCaptureBlock,
} from '../../services/matchdayService';

const LEAGUE_NAMES: Record<LeagueId, string> = {
  liga_mx: 'Liga BBVA MX',
  mundial_fifa: 'Mundial FIFA 2026',
};

interface MatchWithResults extends Match {
  tempScoreA?: number;
  tempScoreB?: number;
}

function getPenaltyAdvancerLabel(match: Match): string | null {
  if (!match.winner_code) return null;
  if (match.team_a_code === match.winner_code) return match.team_a;
  if (match.team_b_code === match.winner_code) return match.team_b ?? null;
  return null;
}

function isDraftKnockoutDraw(match: MatchWithResults): boolean {
  const scoreA = match.tempScoreA;
  const scoreB = match.tempScoreB;
  if (scoreA === undefined || scoreB === undefined) return false;
  return isKnockoutDrawNeedingPenaltyWinner(match, scoreA, scoreB);
}

function renderMatchRows(
  matches: MatchWithResults[],
  league: LeagueId,
  handleScoreChange: (
    matchId: string,
    field: 'tempScoreA' | 'tempScoreB',
    value: number
  ) => void,
  handleSaveResults: (matchId: string) => void,
  loading: boolean
) {
  return matches.map((match) => (
    <Tr key={match.id}>
      <Td whiteSpace="nowrap">
        {league === 'mundial_fifa' ? (
          match.bracket_slot ? (
            <Badge colorScheme="purple" fontSize="xs">
              {match.bracket_slot}
            </Badge>
          ) : match.group ? (
            `Grupo ${match.group}`
          ) : (
            match.phase
          )
        ) : (
          match.phase
        )}
      </Td>
      <Td>
        <VStack align="start" spacing={1}>
          <Text>{match.team_a}</Text>
          {match.score_a !== null &&
            match.score_b !== null &&
            match.score_a === match.score_b &&
            getPenaltyAdvancerLabel(match) && (
              <Badge colorScheme="green" fontSize="xs">
                Avanza: {getPenaltyAdvancerLabel(match)} (pen.)
              </Badge>
            )}
        </VStack>
      </Td>
      <Td>
        <VStack align="start" spacing={1}>
          <Text>{match.team_b}</Text>
          {isDraftKnockoutDraw(match) && !getPenaltyAdvancerLabel(match) && (
            <Badge colorScheme="orange" fontSize="xs">
              Empate 120&apos; — elige quién avanza
            </Badge>
          )}
        </VStack>
      </Td>
      <Td>
        <NumberInput
          min={0}
          max={20}
          size="sm"
          maxW="100px"
          value={match.tempScoreA ?? ''}
          onChange={(_, value) => handleScoreChange(match.id, 'tempScoreA', value)}
        >
          <NumberInputField />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </Td>
      <Td>
        <NumberInput
          min={0}
          max={20}
          size="sm"
          maxW="100px"
          value={match.tempScoreB ?? ''}
          onChange={(_, value) => handleScoreChange(match.id, 'tempScoreB', value)}
        >
          <NumberInputField />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </Td>
      <Td>
        <Button
          colorScheme={isDraftKnockoutDraw(match) ? 'orange' : 'blue'}
          size="sm"
          onClick={() => handleSaveResults(match.id)}
          isLoading={loading}
        >
          {isDraftKnockoutDraw(match) ? 'Guardar empate' : 'Guardar'}
        </Button>
      </Td>
    </Tr>
  ));
}

function MatchResultsTable({
  matches,
  league,
  handleScoreChange,
  handleSaveResults,
  loading,
}: {
  matches: MatchWithResults[];
  league: LeagueId;
  handleScoreChange: (
    matchId: string,
    field: 'tempScoreA' | 'tempScoreB',
    value: number
  ) => void;
  handleSaveResults: (matchId: string) => void;
  loading: boolean;
}) {
  if (matches.length === 0) {
    return (
      <Text color="gray.500" fontSize="sm">
        No hay partidos en esta sección.
      </Text>
    );
  }

  return (
    <ResponsiveTableContainer>
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th>Fase</Th>
            <Th>Local</Th>
            <Th>Visitante</Th>
            <Th>Resultado Local</Th>
            <Th>Resultado Visitante</Th>
            <Th>Acciones</Th>
          </Tr>
        </Thead>
        <Tbody>
          {renderMatchRows(
            matches,
            league,
            handleScoreChange,
            handleSaveResults,
            loading
          )}
        </Tbody>
      </Table>
    </ResponsiveTableContainer>
  );
}

type ScoreHandlers = {
  league: LeagueId;
  handleScoreChange: (
    matchId: string,
    field: 'tempScoreA' | 'tempScoreB',
    value: number
  ) => void;
  handleSaveResults: (matchId: string) => void;
  loading: boolean;
};

function MundialGroupResultsPanels({
  blocks,
  ...handlers
}: { blocks: MundialJornadaCaptureBlock[] } & ScoreHandlers) {
  if (blocks.length === 0) {
    return (
      <Text color="gray.500" fontSize="sm">
        No hay partidos de fase de grupos activos.
      </Text>
    );
  }

  if (blocks.length === 1) {
    return (
      <VStack align="stretch" spacing={6}>
        {blocks[0].groups.map((group) => (
          <Box key={group.key}>
            <Badge mb={3} colorScheme="pitch">
              {group.label}
            </Badge>
            <MatchResultsTable
              matches={group.matches as MatchWithResults[]}
              {...handlers}
            />
          </Box>
        ))}
      </VStack>
    );
  }

  return (
    <Tabs variant="enclosed" colorScheme="pitch" size="sm" isLazy>
      <TabList flexWrap="wrap" gap={1} mb={4}>
        {blocks.map((block) => (
          <Tab key={block.jornada}>{block.label}</Tab>
        ))}
      </TabList>
      <TabPanels>
        {blocks.map((block) => (
          <TabPanel key={block.jornada} px={0}>
            <VStack align="stretch" spacing={6}>
              {block.groups.map((group) => (
                <Box key={group.key}>
                  <Badge mb={3} variant="outline" colorScheme="pitch">
                    {group.label}
                  </Badge>
                  <MatchResultsTable
                    matches={group.matches as MatchWithResults[]}
                    {...handlers}
                  />
                </Box>
              ))}
            </VStack>
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
}

function MundialKnockoutResultsPanels({
  blocks,
  ...handlers
}: { blocks: MundialKnockoutCaptureBlock[] } & ScoreHandlers) {
  if (blocks.length === 0) {
    return (
      <Text color="gray.500" fontSize="sm">
        No hay partidos de eliminatorias activos.
      </Text>
    );
  }

  if (blocks.length === 1) {
    return (
      <MatchResultsTable
        matches={blocks[0].matches as MatchWithResults[]}
        {...handlers}
      />
    );
  }

  return (
    <Tabs variant="enclosed" colorScheme="pitch" size="sm" isLazy>
      <TabList flexWrap="wrap" gap={1} mb={4}>
        {blocks.map((block) => (
          <Tab key={block.phase}>{block.label}</Tab>
        ))}
      </TabList>
      <TabPanels>
        {blocks.map((block) => (
          <TabPanel key={block.phase} px={0}>
            <MatchResultsTable
              matches={block.matches as MatchWithResults[]}
              {...handlers}
            />
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
}

const ManageResults: React.FC = () => {
  const [league, setLeague] = useState<LeagueId>('liga_mx');
  const [activeMatches, setActiveMatches] = useState<MatchWithResults[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncingResults, setSyncingResults] = useState(false);
  const [applyingSync, setApplyingSync] = useState(false);
  const [pendingSyncSummary, setPendingSyncSummary] =
    useState<SyncWorldCupResultsSummary | null>(null);
  const { isOpen: isSyncConfirmOpen, onOpen: onSyncConfirmOpen, onClose: onSyncConfirmClose } =
    useDisclosure();
  const {
    isOpen: isPenaltyDialogOpen,
    onOpen: onPenaltyDialogOpen,
    onClose: onPenaltyDialogClose,
  } = useDisclosure();
  const cancelSyncRef = useRef<HTMLButtonElement>(null);
  const [penaltyRequest, setPenaltyRequest] = useState<PenaltyWinnerRequest | null>(null);
  const [penaltyQueue, setPenaltyQueue] = useState<PenaltyWinnerRequest[]>([]);
  const toast = useToast();
  const { user } = useAuth();

  useEffect(() => {
    loadActiveMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league]);

  const loadActiveMatches = async () => {
    try {
      const matchesRef = collection(db, 'matches');
      const q = query(
        matchesRef,
        where('activo', '==', true),
        orderBy('date', 'asc')
      );
      const matchesSnap = await getDocs(q);
      const matchesData = matchesSnap.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({
        id: doc.id,
        ...doc.data(),
        tempScoreA: doc.data().score_a,
        tempScoreB: doc.data().score_b,
      })) as MatchWithResults[];

      const filtered = matchesData.filter(
        (m) => m.league === league || (league === 'liga_mx' && !m.league)
      );

      const visible =
        league === 'mundial_fifa' ? filterActiveMundialMatches(filtered) : filtered;

      const displayMatches =
        league === 'mundial_fifa'
          ? await hydrateMundialMatchesAsync(visible)
          : visible;

      setActiveMatches(displayMatches as MatchWithResults[]);
    } catch (error) {
      console.error('Error al cargar partidos:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los partidos activos',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleScoreChange = (
    matchId: string,
    field: 'tempScoreA' | 'tempScoreB',
    value: number
  ) => {
    setActiveMatches((prevMatches) =>
      prevMatches.map((match) =>
        match.id === matchId ? { ...match, [field]: value } : match
      )
    );
  };

  const persistMatchResult = async (
    match: MatchWithResults,
    scoreA: number,
    scoreB: number,
    options?: {
      skipPopulateRoundOf32?: boolean;
      winnerCode?: string | null;
      skipAdvance?: boolean;
    }
  ) => {
    const matchRef = doc(db, 'matches', match.id);
    const yaExisteResultado = match.score_a !== null && match.score_b !== null;
    const isDraw = isKnockoutDrawNeedingPenaltyWinner(match, scoreA, scoreB);
    const winnerCode =
      options?.winnerCode !== undefined ? options.winnerCode : isDraw ? match.winner_code : null;

    const updatePayload: {
      score_a: number;
      score_b: number;
      updated_at: Date;
      winner_code?: string | null;
    } = {
      score_a: scoreA,
      score_b: scoreB,
      updated_at: new Date(),
    };

    if (isDraw) {
      if (!winnerCode) {
        await updateDoc(matchRef, updatePayload);
        return yaExisteResultado;
      }
      updatePayload.winner_code = winnerCode;
    } else {
      updatePayload.winner_code = null;
    }

    await updateDoc(matchRef, updatePayload);

    const updatedMatch: Match = {
      ...match,
      score_a: scoreA,
      score_b: scoreB,
      winner_code: isDraw ? winnerCode ?? null : null,
    };

    if (league === 'mundial_fifa' && !options?.skipAdvance && (!isDraw || winnerCode)) {
      await mundialBracketService.advanceWinnerFromMatch(updatedMatch);
    }

    return yaExisteResultado;
  };

  const openPenaltyWinnerFlow = (requests: PenaltyWinnerRequest[]) => {
    if (requests.length === 0) return;
    setPenaltyQueue(requests);
    setPenaltyRequest(requests[0]);
    onPenaltyDialogOpen();
  };

  const handlePenaltyWinnerConfirm = async (winnerCode: string) => {
    if (!penaltyRequest) return;

    setLoading(true);
    try {
      const yaExisteResultado = await persistMatchResult(
        penaltyRequest.match as MatchWithResults,
        penaltyRequest.scoreA,
        penaltyRequest.scoreB,
        { winnerCode }
      );

      toast({
        title: 'Resultado guardado',
        description: yaExisteResultado
          ? 'Empate y avance por penales actualizados'
          : 'Marcador tras 120 min y avance por penales registrados',
        status: 'success',
        duration: 3000,
      });

      const [, ...rest] = penaltyQueue;
      setPenaltyQueue(rest);
      if (rest.length > 0) {
        setPenaltyRequest(rest[0]);
        await loadActiveMatches();
      } else {
        setPenaltyRequest(null);
        onPenaltyDialogClose();
        await loadActiveMatches();
      }
    } catch (error) {
      console.error('Error al guardar avance por penales:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar el avance por penales',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePenaltyWinnerCancel = () => {
    setPenaltyRequest(null);
    setPenaltyQueue([]);
    onPenaltyDialogClose();
  };

  const handleSaveResults = async (matchId: string) => {
    const match = activeMatches.find((m) => m.id === matchId);
    if (!match) return;

    if (match.tempScoreA === undefined || match.tempScoreB === undefined) {
      toast({
        title: 'Error',
        description: 'Por favor ingresa ambos resultados',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (isKnockoutDrawNeedingPenaltyWinner(match, match.tempScoreA, match.tempScoreB)) {
      openPenaltyWinnerFlow([
        {
          match,
          scoreA: match.tempScoreA,
          scoreB: match.tempScoreB,
        },
      ]);
      return;
    }

    setLoading(true);
    try {
      const yaExisteResultado = await persistMatchResult(
        match,
        match.tempScoreA,
        match.tempScoreB
      );

      toast({
        title: 'Éxito',
        description: yaExisteResultado
          ? 'Resultado actualizado correctamente'
          : 'Resultado guardado correctamente',
        status: 'success',
        duration: 3000,
      });

      loadActiveMatches();
    } catch (error) {
      console.error('Error al guardar resultado:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar el resultado',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSyncMundialResults = async () => {
    setSyncingResults(true);
    try {
      const mundialMatches = await mundialMatchService.getMundialMatchesHydrated();

      const events = await fetchWorldCup2026Events();
      const summary = syncWorldCupResultsFromEvents(events, mundialMatches);

      setPendingSyncSummary(summary);
      onSyncConfirmOpen();
    } catch (error) {
      console.error('Error al sincronizar resultados del mundial:', error);
      sileo.error({
        title: 'Error al sincronizar',
        description:
          error instanceof Error
            ? error.message
            : 'No se pudieron obtener los resultados desde TheSportsDB',
      });
    } finally {
      setSyncingResults(false);
    }
  };

  const handleConfirmSync = async () => {
    if (!pendingSyncSummary || pendingSyncSummary.updated.length === 0) return;

    setApplyingSync(true);
    try {
      const drawRequests: PenaltyWinnerRequest[] = [];

      for (const item of pendingSyncSummary.updated) {
        if (isKnockoutDrawNeedingPenaltyWinner(item.match, item.scoreA, item.scoreB)) {
          await persistMatchResult(item.match, item.scoreA, item.scoreB, {
            skipPopulateRoundOf32: true,
            skipAdvance: true,
          });
          drawRequests.push({
            match: item.match,
            scoreA: item.scoreA,
            scoreB: item.scoreB,
          });
        } else {
          await persistMatchResult(item.match, item.scoreA, item.scoreB, {
            skipPopulateRoundOf32: true,
          });
        }
      }

      const totalUpdated = pendingSyncSummary.updated.length;

      const updatedLabels = pendingSyncSummary.updated
        .map(
          (item) =>
            `${item.match.team_a} ${item.scoreA}-${item.scoreB} ${item.match.team_b}`
        )
        .join(', ');

      setPendingSyncSummary(null);
      onSyncConfirmClose();

      if (drawRequests.length > 0) {
        sileo.success({
          title: 'Marcadores guardados',
          description: `${totalUpdated} resultado(s) aplicados. Indica quién avanzó por penales en ${drawRequests.length} empate(s).`,
        });
        openPenaltyWinnerFlow(drawRequests);
      } else {
        await loadActiveMatches();
        sileo.success({
          title: 'Resultados guardados',
          description: `Se aplicaron ${totalUpdated} resultado(s): ${updatedLabels}`,
        });
      }
    } catch (error) {
      console.error('Error al aplicar resultados sincronizados:', error);
      sileo.error({
        title: 'Error al guardar',
        description: 'No se pudieron guardar los resultados sincronizados',
      });
    } finally {
      setApplyingSync(false);
    }
  };

  const handleCancelSync = () => {
    setPendingSyncSummary(null);
    onSyncConfirmClose();
  };

  const omittedWithoutScore =
    pendingSyncSummary?.skipped.filter((s) =>
      s.reason.includes('Sin resultado oficial')
    ).length ?? 0;

  const pendingToSave = pendingSyncSummary?.updated.length ?? 0;
  const officialResultsCount = pendingSyncSummary?.officialResults.length ?? 0;

  const mundialLayout = useMemo(() => {
    if (league !== 'mundial_fifa') return null;
    const groupMatches = getMundialGroupMatches(activeMatches);
    const knockoutMatches = getMundialKnockoutMatches(activeMatches);
    return {
      groupBlocks: groupMundialMatchesByJornada(groupMatches, groupMatches),
      knockoutBlocks: groupMundialKnockoutByPhase(knockoutMatches),
      hasGroups: groupMatches.length > 0,
      hasKnockout: knockoutMatches.length > 0,
    };
  }, [league, activeMatches]);

  const mundialActiveLabel = useMemo(() => {
    if (league !== 'mundial_fifa') return '';
    const groupPool = getMundialGroupMatches(activeMatches);
    const jornadas = getActiveMundialJornadas(groupPool);
    const knockoutPhases = getActiveMundialKnockoutPhases(
      getMundialKnockoutMatches(activeMatches)
    );
    if (jornadas.length === 0 && knockoutPhases.length === 0) {
      return 'No hay fase activa. Activa jornadas o eliminatorias en Gestionar Partidos.';
    }
    return `Mostrando partidos activos: ${formatMundialCaptureLabel(jornadas, knockoutPhases)}`;
  }, [league, activeMatches]);

  const statusBadgeColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'green';
      case 'already_saved':
        return 'gray';
      case 'unmatched':
        return 'orange';
      default:
        return 'red';
    }
  };

  if (!user || user.email !== 'htorres@teiker.mx') {
    return (
      <Box p={4}>
        <Heading size="md">No tienes acceso a esta página</Heading>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch" p={{ base: 2, sm: 4 }}>
      <Heading>Administración de Resultados</Heading>

      <FormControl maxW="xs">
        <FormLabel>Liga / Torneo</FormLabel>
        <Select value={league} onChange={(e) => setLeague(e.target.value as LeagueId)}>
          <option value="liga_mx">{LEAGUE_NAMES.liga_mx}</option>
          <option value="mundial_fifa">{LEAGUE_NAMES.mundial_fifa}</option>
        </Select>
      </FormControl>

      {league === 'mundial_fifa' && (
        <Flex justify="space-between" align={{ base: 'stretch', md: 'center' }} gap={3} wrap="wrap">
          <Text fontSize="sm" color="gray.600">
            {mundialActiveLabel}
          </Text>
          <Button
            colorScheme="green"
            size="sm"
            w={{ base: 'full', md: 'auto' }}
            onClick={handleSyncMundialResults}
            isLoading={syncingResults}
            loadingText="Consultando API..."
          >
            Sincronizar resultados reales
          </Button>
        </Flex>
      )}

      <Card>
        <CardHeader>
          <Heading size="md">Partidos Activos - {LEAGUE_NAMES[league]}</Heading>
        </CardHeader>
        <CardBody>
          {activeMatches.length === 0 ? (
            <Text color="gray.500">No hay partidos activos para esta liga.</Text>
          ) : league === 'mundial_fifa' && mundialLayout ? (
            <Tabs variant="soft-rounded" colorScheme="pitch" size="sm" isLazy>
              {mundialLayout.hasGroups && mundialLayout.hasKnockout ? (
                <>
                  <TabList flexWrap="wrap" gap={1} mb={4}>
                    <Tab>Fase de grupos</Tab>
                    <Tab>Eliminatorias</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel px={0}>
                      <MundialGroupResultsPanels
                        blocks={mundialLayout.groupBlocks}
                        league={league}
                        handleScoreChange={handleScoreChange}
                        handleSaveResults={handleSaveResults}
                        loading={loading}
                      />
                    </TabPanel>
                    <TabPanel px={0}>
                      <MundialKnockoutResultsPanels
                        blocks={mundialLayout.knockoutBlocks}
                        league={league}
                        handleScoreChange={handleScoreChange}
                        handleSaveResults={handleSaveResults}
                        loading={loading}
                      />
                    </TabPanel>
                  </TabPanels>
                </>
              ) : mundialLayout.hasGroups ? (
                <MundialGroupResultsPanels
                  blocks={mundialLayout.groupBlocks}
                  league={league}
                  handleScoreChange={handleScoreChange}
                  handleSaveResults={handleSaveResults}
                  loading={loading}
                />
              ) : (
                <MundialKnockoutResultsPanels
                  blocks={mundialLayout.knockoutBlocks}
                  league={league}
                  handleScoreChange={handleScoreChange}
                  handleSaveResults={handleSaveResults}
                  loading={loading}
                />
              )}
            </Tabs>
          ) : (
            <MatchResultsTable
              matches={activeMatches}
              league={league}
              handleScoreChange={handleScoreChange}
              handleSaveResults={handleSaveResults}
              loading={loading}
            />
          )}
        </CardBody>
      </Card>

      <AlertDialog
        isOpen={isSyncConfirmOpen}
        leastDestructiveRef={cancelSyncRef}
        onClose={handleCancelSync}
        size={{ base: 'full', md: '4xl' }}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent mx={{ base: 2, md: 4 }}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirmar resultados reales
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={3}>
                La API reporta{' '}
                <strong>{officialResultsCount}</strong> resultado(s) final(es).
                {pendingToSave > 0 ? (
                  <>
                    {' '}
                    <strong>{pendingToSave}</strong> nuevo(s) listo(s) para guardar.
                  </>
                ) : (
                  ' Ninguno requiere actualización.'
                )}
                {omittedWithoutScore > 0 && (
                  <Text as="span" color="gray.500" display="block" mt={1} fontSize="sm">
                    {omittedWithoutScore} partido(s) en la API aún sin marcador final (quedan
                    abiertos).
                  </Text>
                )}
              </Text>
              {officialResultsCount === 0 ? (
                <Text color="gray.500" fontSize="sm">
                  Aún no hay resultados finales en TheSportsDB para esta temporada.
                </Text>
              ) : (
                <TableContainer maxH="360px" overflowY="auto" overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead position="sticky" top={0} bg="gray.50" zIndex={1}>
                      <Tr>
                        <Th>Fecha</Th>
                        <Th>Partido (API)</Th>
                        <Th>Resultado</Th>
                        <Th>Partido local</Th>
                        <Th>Actual</Th>
                        <Th>Estado</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {pendingSyncSummary?.officialResults.map((item) => {
                        const current =
                          item.match &&
                          item.match.score_a !== null &&
                          item.match.score_b !== null
                            ? `${item.match.score_a} - ${item.match.score_b}`
                            : '—';
                        const localMatch = item.match
                          ? `${item.match.team_a} vs ${item.match.team_b}`
                          : '—';
                        const resultLabel =
                          item.scoreA !== null && item.scoreB !== null
                            ? `${item.scoreA} - ${item.scoreB}`
                            : '—';

                        return (
                          <Tr key={item.event.idEvent}>
                            <Td whiteSpace="nowrap">{item.event.dateEvent}</Td>
                            <Td>{item.event.strEvent}</Td>
                            <Td fontWeight="semibold">{resultLabel}</Td>
                            <Td>{localMatch}</Td>
                            <Td color="gray.500">{current}</Td>
                            <Td>
                              <Badge colorScheme={statusBadgeColor(item.status)}>
                                {item.statusLabel}
                              </Badge>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
              <Text mt={3} fontSize="sm" color="gray.500">
                Los partidos sin resultado en la respuesta no se modifican.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelSyncRef} onClick={handleCancelSync}>
                {pendingToSave > 0 ? 'Cancelar' : 'Cerrar'}
              </Button>
              {pendingToSave > 0 && (
                <Button
                  colorScheme="green"
                  onClick={handleConfirmSync}
                  ml={3}
                  isLoading={applyingSync}
                  loadingText="Guardando..."
                >
                  Aceptar y guardar ({pendingToSave})
                </Button>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <KnockoutPenaltyWinnerDialog
        request={penaltyRequest}
        isOpen={isPenaltyDialogOpen}
        isLoading={loading}
        onConfirm={handlePenaltyWinnerConfirm}
        onCancel={handlePenaltyWinnerCancel}
      />
    </VStack>
  );
};

export default ManageResults;
